import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { eur, itDate } from "@/components/modular/PaymentParts";
import { PaymentPlanDialog } from "@/components/modular/PaymentPlanDialog";
import { WelcomeButton } from "@/components/modular/WelcomeButton";

function Kpi({ label, value, testId, tone = "" }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${tone}`} data-testid={testId}>{value}</div>
    </Card>
  );
}

export default function PagamentiModulare() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const { data } = useQuery({ queryKey: ["mod-payments"], queryFn: async () => (await api.get("/modular-payments")).data });
  const rows = (data?.rows || []).filter((r) => {
    if (tab === "overdue" && !r.plan?.overdue_count) return false;
    if (tab === "noplan" && r.plan) return false;
    if (tab === "ok" && (!r.plan || r.plan.overdue_count)) return false;
    return !q.trim() || `${r.student.last_name} ${r.student.first_name} ${r.student.matricola || ""}`.toLowerCase().includes(q.trim().toLowerCase());
  });
  const t = data?.totals || {};
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto space-y-5" data-testid="mod-payments-page">
      <PageHeader title="Pagamenti Scuola Modulare" subtitle="Piani di pagamento, rate pagate e scadute. Solleciti via email o WhatsApp; email automatica sulle rate scadute ogni 7 giorni."/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Atteso" value={eur(t.expected)} testId="mp-kpi-expected"/>
        <Kpi label="Incassato" value={eur(t.paid)} testId="mp-kpi-paid" tone="text-emerald-700"/>
        <Kpi label="Scaduto" value={eur(t.overdue)} testId="mp-kpi-overdue" tone="text-red-700"/>
        <Kpi label="Studenti in ritardo" value={t.overdue_students ?? 0} testId="mp-kpi-overdue-students"/>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="mp-tab-all">Tutti</TabsTrigger>
            <TabsTrigger value="overdue" data-testid="mp-tab-overdue">In ritardo</TabsTrigger>
            <TabsTrigger value="ok" data-testid="mp-tab-ok">In regola</TabsTrigger>
            <TabsTrigger value="noplan" data-testid="mp-tab-noplan">Senza piano</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca studente o matricola" className="pl-8" data-testid="mp-search"/></div>
        <WelcomeButton rows={data?.rows || []}/>
      </div>
      <Card className="border border-stone-200 rounded-xl shadow-none divide-y divide-stone-100">
        {rows.map(({ student: s, plan: p }) => (
          <button key={s.id} onClick={() => setOpen(s.id)} className="w-full text-left p-4 flex flex-wrap items-center gap-3 hover:bg-stone-50 transition-colors" data-testid={`mp-row-${s.id}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="font-medium text-[#1C352D]">{s.last_name} {s.first_name} <span className="text-xs text-stone-400 font-normal">{s.matricola}</span></div>
              <div className="text-xs text-stone-500">{p ? `${p.plan_label} · prossima: ${p.next_due ? `${eur(p.next_due.amount)} il ${itDate(p.next_due.due_date)}` : "nessuna"}` : "Nessun piano di pagamento"}</div>
            </div>
            {p && <div className="text-sm tabular-nums text-right"><div>{eur(p.paid)} / {eur(p.total)}</div>
              {p.overdue_count > 0 ? <div className="text-xs text-red-700">{p.overdue_count} scadut{p.overdue_count === 1 ? "a" : "e"} · {eur(p.overdue_amount)}</div> : <div className="text-xs text-emerald-700">in regola</div>}</div>}
            {p && <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${p.welcome_sent_at ? "bg-sky-100 text-sky-800" : "bg-stone-100 text-stone-500"}`} data-testid={`mp-welcome-${s.id}`}>
              {p.welcome_sent_at ? `Welcome inviata ${itDate(p.welcome_sent_at)}` : "Welcome non inviata"}</span>}
          </button>
        ))}
        {!rows.length && <div className="p-8 text-center text-sm text-stone-400">Nessuno studente.</div>}
      </Card>
      {open && <PaymentPlanDialog studentId={open} onClose={() => setOpen(null)}/>}
    </div>
  );
}
